import { Color } from '@persona-id/design-tokens';
import {
  CircleCheckIcon,
  CircleMinusIcon,
  CircleXIcon,
  MoreIcon,
} from '@persona-id/design-tokens/icons';
import { lowerCase, upperFirst } from 'lodash/fp';
import { ReactNode, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import ClickableDetail from 'dashboard/components/ClickableDetail';
import FlagIssuesModal from 'dashboard/components/FlagIssuesModal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { SimulatedDataSticker } from 'dashboard/components/SimulatedDataSticker/SimulatedDataSticker';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import AamvaVerification from 'dashboard/components/VerificationDetail/VerificationTypes/AamvaVerification/AamvaVerification';
import BusinessWebsiteVerification from 'dashboard/components/VerificationDetail/VerificationTypes/BusinessWebsiteVerification/BusinessWebsiteVerification';
import CertificateKoreaVerification from 'dashboard/components/VerificationDetail/VerificationTypes/CertificateKoreaVerification';
import DatabaseAadhaarVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseAadhaarVerification/DatabaseAadhaarVerification';
import { DatabaseBusinessFootprintVerification } from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseBusinessFootprintVerification/DatabaseBusinessFootprintVerification';
import { DatabaseBusinessVerification } from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseBusinessVerification/DatabaseBusinessVerification';
import DatabaseEcbsvVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseEcbsvVerification/DatabaseEcbsvVerification';
import DatabasePhoneCarrierVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DatabasePhoneCarrierVerification/DatabasePhoneCarrierVerification';
import DatabaseSerproVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseSerproVerification/DatabaseSerproVerification';
import DatabaseStandardVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseStandardVerification/DatabaseStandardVerification';
import DatabaseTinVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseTinVerification/DatabaseTinVerification';
import DatabaseVatNumberVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseVatNumberVerification/DatabaseVatNumberVerification';
import DatabaseVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseVerification/DatabaseVerification';
import DigitalIdSerproVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DigitalIdSerproVerification';
import DigitalIdUkSharecodeVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DigitalIdUkSharecodeVerification';
import GADocumentVerification from 'dashboard/components/VerificationDetail/VerificationTypes/DocumentVerification';
import EmailAddressVerification from 'dashboard/components/VerificationDetail/VerificationTypes/EmailAddressVerification';
import GovernmentIdNfcVerification from 'dashboard/components/VerificationDetail/VerificationTypes/GovernmentIdNfcVerification';
import GovernmentIdVerification from 'dashboard/components/VerificationDetail/VerificationTypes/GovernmentIdVerification';
import MobileDriversLicenseVerification from 'dashboard/components/VerificationDetail/VerificationTypes/MobileDriversLicenseVerification';
import PhoneNumberVerification from 'dashboard/components/VerificationDetail/VerificationTypes/PhoneNumberVerification';
import SelfieVerification from 'dashboard/components/VerificationDetail/VerificationTypes/SelfieVerification';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { VERIFICATION_TYPE_NAMES } from 'dashboard/lib/constants/verification';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import copyToClipboard from 'dashboard/lib/utils/copyToClipboard';
import { formatTimeRangeDuration } from 'dashboard/lib/utils/formatters';
import { Document, DocumentGeneric } from 'dashboard/models/document';
import { EnvironmentType } from 'dashboard/models/environment';
import {
  Verification,
  VERIFICATION_API_MODEL_TYPES_WITH_QA_VERIFICATION,
} from 'dashboard/models/verification';
import {
  GovernmentIdVerificationModuleAttribute,
  ResourceVerificationAttributes,
  SelfieVerificationModuleAttribute,
} from 'dashboard/modules/ResourceVerification/model';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';
import { VerificationStatus } from 'lib/verification';

const StyledMenu = styled(DropdownMenu)`
  min-width: 200px;
  align-content: center;
`;

const TabHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const IdAndTimeRangeContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-right: 20px;
`;

const TitleDetailContainer = styled('div')`
  display: flex;
  align-items: center;
`;

interface VerificationDetailContainerProps {
  layout?: ResourceVerificationAttributes[];
  toggleable?: boolean;
  isDefaultOpen?: boolean;
  documents: Document[];
  environmentType: EnvironmentType;
  exposeId?: boolean;
  handleFlagVerificationIssues?: (
    verification: Verification,
    values: FlagIssuesFormData,
  ) => Promise<void> | void;
}

const VerificationDetail = ({
  layout,
  verification,
  documents,
  environmentType,
  toggleable = true,
  isDefaultOpen = true,
  exposeId = false,
  handleFlagVerificationIssues,
}: VerificationDetailProps & VerificationDetailContainerProps) => {
  const dispatch = useDispatch();

  const [
    reportVerificationIssue,
    { enable: showReportVerificationIssue, disable: hideReportVerificationIssue },
  ] = useToggle(false);

  const copyField = useCallback(async () => {
    const copyWasSuccessful = await copyToClipboard(verification.id);
    if (copyWasSuccessful) {
      dispatch(showMessage('Copied Verification ID', MessageType.Success));
    } else {
      dispatch(
        showMessage('Error while copying verification ID, please try again.', MessageType.Failure),
      );
    }
  }, [verification.id, dispatch]);

  const missingQaVerificationSupport = !VERIFICATION_API_MODEL_TYPES_WITH_QA_VERIFICATION.includes(
    verification.type,
  );

  const { 'created-at': createdAt, 'completed-at': completedAt, status } = verification.attributes;

  const verificationId = verification.id;

  const { completionTime, timeRange } = formatTimeRangeDuration(createdAt, completedAt);

  const handleFlagIssuesAndClose = useCallback(
    async (values: FlagIssuesFormData) => {
      await handleFlagVerificationIssues?.(verification, values);
      hideReportVerificationIssue();
    },
    [handleFlagVerificationIssues, hideReportVerificationIssue, verification],
  );

  const moreItems = useMemo(() => {
    return [
      {
        id: 'verification-details-header__copy-verification-id',
        name: 'verification-details-header__copy-verification-id',
        label: 'Copy verification ID',
        variant: 'basic' as const,
        disabled: false,
        onClick: copyField,
      },
      {
        id: 'verification-details-header__report-issue',
        name: 'verification-details-header__report-issue',
        label: 'Report issue',
        variant: 'basic' as const,
        disabled: false,
        onClick: showReportVerificationIssue,
        hidden: missingQaVerificationSupport,
      },
    ].filter((item) => !item.hidden);
  }, [missingQaVerificationSupport, copyField, showReportVerificationIssue]);

  return (
    <>
      <ClickableDetail
        header={
          <TabHeader>
            <TitleDetailContainer>
              <Title
                verification={verification}
                status={status as VerificationStatus}
                documents={documents}
              />
              {environmentType === ApiModel.EnvironmentSandbox && (
                <SimulatedDataSticker dataSectionLabel='gov-id-verif' />
              )}
            </TitleDetailContainer>
            <IdAndTimeRangeContainer>
              <Label size='md' color='tertiary' weight='bold'>
                {exposeId
                  ? (verification.attributes['verification-template-name'] ?? verificationId) +
                    ' | '
                  : ''}
                {timeRange}
              </Label>
              {completionTime != null && (
                <>
                  <Spacer height={0} width={1} />
                  <Label size='md' color='tertiary' weight='bold'>
                    {completionTime}
                  </Label>
                </>
              )}
            </IdAndTimeRangeContainer>
          </TabHeader>
        }
        dropdownMenu={
          <StyledMenu
            trigger={
              <Button
                id='resource-verification-menu__open-menu-button'
                width='icon'
                variant='inverse'
                aria-label='More actions'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <MoreIcon />
              </Button>
            }
          >
            {moreItems.map((item) => (
              <DropdownMenu.Item
                key={item.id}
                id={item.id}
                variant={item.variant}
                onClick={(e) => {
                  e.stopPropagation();
                  item.onClick();
                }}
                disabled={item.disabled}
              >
                {item.label}
              </DropdownMenu.Item>
            ))}
          </StyledMenu>
        }
        disabled={!toggleable}
        isDefaultOpen={isDefaultOpen}
      >
        {getTypeDetail(verification, documents, layout)}
      </ClickableDetail>

      <ModalWrapper isOpen={reportVerificationIssue} handleCloseModal={hideReportVerificationIssue}>
        <FlagIssuesModal
          verifications={[verification]}
          documents={documents}
          handleClose={hideReportVerificationIssue}
          handleSubmitFlagIssues={handleFlagIssuesAndClose}
        />
      </ModalWrapper>
    </>
  );
};

const getTypeDetail = (
  verification: Verification,
  documents: Document[],
  layout?: ResourceVerificationAttributes[],
): ReactNode => {
  switch (verification.type) {
    case ApiModel.VerificationAamva:
      return <AamvaVerification verification={verification} />;
    case ApiModel.VerificationBusinessWebsite:
      return <BusinessWebsiteVerification verification={verification} />;
    case ApiModel.VerificationCertificateKorea:
      return <CertificateKoreaVerification verification={verification} />;
    case ApiModel.VerificationDatabase:
      return <DatabaseVerification verification={verification} />;
    case ApiModel.VerificationDatabaseAadhaar:
      return <DatabaseAadhaarVerification verification={verification} />;
    case ApiModel.VerificationDatabaseStandard:
      return <DatabaseStandardVerification verification={verification} />;
    case ApiModel.VerificationDatabaseBusiness:
      return <DatabaseBusinessVerification verification={verification} />;
    case ApiModel.VerificationDatabaseBusinessFootprint:
      return <DatabaseBusinessFootprintVerification verification={verification} />;
    case ApiModel.VerificationDatabaseEcbsv:
      return <DatabaseEcbsvVerification verification={verification} />;
    case ApiModel.VerificationDatabasePhoneCarrier:
      return <DatabasePhoneCarrierVerification verification={verification} />;
    case ApiModel.VerificationDatabaseSerpro:
      return <DatabaseSerproVerification verification={verification} />;
    case ApiModel.VerificationDatabaseTin:
      return <DatabaseTinVerification verification={verification} />;
    case ApiModel.VerificationDatabaseVatNumber:
      return <DatabaseVatNumberVerification verification={verification} />;
    case ApiModel.VerificationDigitalIdSerpro:
      return <DigitalIdSerproVerification verification={verification} />;
    case ApiModel.VerificationDigitalIdSingpass:
      // TODO(apark): Implement more detailed view for Singpass
      return <></>;
    case ApiModel.VerificationDigitalIdUkSharecode:
      return <DigitalIdUkSharecodeVerification verification={verification} />;
    case ApiModel.VerificationDigitalIdFinnishTrustNetwork:
      // TODO(apark): Implement more detailed view for Finnish Trust Network
      return <></>;
    case ApiModel.VerificationDigitalIdOneId:
      // TODO(apark): Implement more detailed view for OneID
      return <></>;
    case ApiModel.VerificationGovernmentId:
      return (
        <GovernmentIdVerification
          layout={layout as GovernmentIdVerificationModuleAttribute[]}
          verification={verification}
        />
      );
    case ApiModel.VerificationPhoneNumber:
      return (
        <PhoneNumberVerification
          data-test='verification-detail__phone-number-verification'
          verification={verification}
        />
      );
    case ApiModel.VerificationEmailAddress:
      return (
        <EmailAddressVerification
          data-test='verification-detail__email-verification'
          verification={verification}
        />
      );
    case ApiModel.VerificationSelfie:
      return (
        <SelfieVerification
          layout={layout as SelfieVerificationModuleAttribute[]}
          verification={verification}
        />
      );
    case ApiModel.VerificationGovernmentIdNfc:
      return <GovernmentIdNfcVerification verification={verification} />;
    case ApiModel.VerificationMobileDriversLicense:
      return <MobileDriversLicenseVerification verification={verification} />;
    case ApiModel.VerificationDocument: {
      const document = documents.find(
        (doc): doc is DocumentGeneric => verification.relationships.document?.data.id === doc.id,
      );
      return document && <GADocumentVerification verification={verification} document={document} />;
    }
    default:
      assertNever(verification);
  }
};

const iconProps = {
  height: 20,
  width: 20,
};

interface TitleProps {
  verification: Verification;
  status: VerificationStatus;
  documents: Document[];
}

const TitleContainer = styled.div<{ isFailed: boolean }>`
  display: flex;
  align-items: center;

  ${({ isFailed, theme }) =>
    isFailed &&
    css`
      color: ${theme.color.variants.danger.default};
    `};
`;

const Title = ({ verification, status, documents }: TitleProps) => {
  let icon: ReactNode;
  switch (status) {
    case VerificationStatus.Initiated:
    case VerificationStatus.Confirmed:
    case VerificationStatus.Submitted:
    case VerificationStatus.TentativelyPassed:
    case VerificationStatus.TentativelyFailed:
    case VerificationStatus.Skipped:
      icon = <CircleMinusIcon color={Color.BorderIconWarning} {...iconProps} />;
      break;

    case VerificationStatus.Passed:
      icon = <CircleCheckIcon color={Color.BorderIconSuccess} {...iconProps} />;
      break;

    case VerificationStatus.RequiresRetry:
    case VerificationStatus.Failed:
    case VerificationStatus.Canceled:
      icon = <CircleXIcon color={Color.BorderIconDanger} {...iconProps} />;
      break;

    default:
      assertNever(status);
  }

  let heading: string;
  const defaultHeading = () => `${VERIFICATION_TYPE_NAMES[verification.type]} ${lowerCase(status)}`;
  switch (verification.type) {
    case ApiModel.VerificationDocument: {
      const objectDocument = documents.find(
        (doc): doc is DocumentGeneric => verification.relationships.document?.data.id === doc.id,
      );
      heading = objectDocument
        ? `Document AI - ${upperFirst(lowerCase(objectDocument.attributes.kind))}`
        : defaultHeading();
      break;
    }
    default:
      heading = defaultHeading();
  }

  return (
    <TitleContainer
      isFailed={[
        VerificationStatus.RequiresRetry,
        VerificationStatus.Failed,
        VerificationStatus.Canceled,
      ].includes(status)}
    >
      {icon}
      <Spacer height={0} width={2.5} />
      <Heading size='sm' color={null}>
        {heading}
      </Heading>
    </TitleContainer>
  );
};

export default VerificationDetail;
